import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import {
  getDashboardDataAPI,
  updateSupportTicketReadStatusAPI,
} from "../../utils/api";
import {
  FaUsers,
  FaShoppingCart,
  FaBox,
  FaTags,
  FaStar,
  FaImage,
  FaGlobe,
  FaUserTie,
  FaClipboardCheck,
  FaCalendarMinus,
  FaCalendarCheck,
  FaVideo,
} from "react-icons/fa";
import "./Dashboard.css";
import { useDispatch, useSelector } from "react-redux";
import { setNewTicketOrReply } from "../../store/slices/supportTicketState";
import OrdersCharts from "./components/OrdersCharts";

/** @typedef {'1 week' | '1 month' | '6 month' | '1 year' | 'custom'} DashboardTrendType */

/** Matches Laravel `type` rule: in:1 week,1 month,6 month,1 year,custom */
const TREND_RANGE_OPTIONS = [
  { value: "1 week", label: "1 week" },
  { value: "1 month", label: "1 month" },
  { value: "6 month", label: "6 month" },
  { value: "1 year", label: "1 year" },
  { value: "custom", label: "custom" },
];

function formatApiDateDDMMYYYY(m) {
  return m.clone().format("DD-MM-YYYY");
}

/**
 * Body for `POST admin/dashboard/data/get`.
 * Preset: `{ type: "1 week" | … }`. Custom: `{ type: "custom", from_date, to_date }` DD-MM-YYYY.
 */
function buildDashboardPostBody(apiType, customFromISO, customToISO) {
  const type = typeof apiType === "string" && apiType.trim() ? apiType.trim() : "6 month";

  if (type === "custom") {
    let from = moment(customFromISO, "YYYY-MM-DD", true).startOf("day");
    let to = moment(customToISO, "YYYY-MM-DD", true).endOf("day");
    if (!from.isValid()) from = moment().subtract(5, "days").startOf("day");
    if (!to.isValid()) to = moment().endOf("day");
    if (from.isAfter(to)) {
      const a = from.clone();
      from = to.clone().startOf("day");
      to = a.clone().endOf("day");
    }
    return {
      type: "custom",
      from_date: formatApiDateDDMMYYYY(from),
      to_date: formatApiDateDDMMYYYY(to),
    };
  }

  return { type };
}

const ORDER_SUMMARY_DEFS = [
  {
    key: "active-orders",
    label: "Active orders",
    link: "/Order",
    Icon: FaShoppingCart,
    /* Count = total_order from API order_stats */
    matchNames: [
      "pending order",
      "pending orders",
      "Pending order",
      "Pending orders",
      "Pending Orders",
      "pending",
      "active orders",
      "active order",
      "Active Orders",
    ],
    iconTone: "order-stat-tone--warm",
  },
  {
    key: "completed",
    label: "Completed",
    link: "/Order",
    Icon: FaClipboardCheck,
    /* Count = completed orders from API */
    matchNames: [
      "completd",
      "completed",
      "Completed",
      "completed order",
      "completed orders",
      "Completed order",
      "Completed Orders",
    ],
    iconTone: "order-stat-tone--mint",
  },
  {
    key: "yesterday",
    label: "Yesterday",
    link: "/Order",
    Icon: FaCalendarMinus,
    matchNames: [
      "yesterday",
      "Yesterday",
      "yesterday orders",
      "Yesterday orders",
      "orders yesterday",
      "Orders yesterday",
    ],
    iconTone: "order-stat-tone--sky",
  },
  {
    key: "today",
    label: "Today",
    link: "/Order",
    Icon: FaCalendarCheck,
    matchNames: [
      "today",
      "Today",
      "today orders",
      "Today orders",
      "todays orders",
      "Today's orders",
    ],
    iconTone: "order-stat-tone--rose",
  },
];

function pickCount(rows, matchNames) {
  if (!Array.isArray(rows)) return 0;
  const lowered = matchNames.map((n) => n.toLowerCase().trim());
  const hit = rows.find((r) => {
    const name = String(r?.name ?? "").toLowerCase().trim();
    return lowered.includes(name);
  });
  return Number(hit?.count ?? 0) || 0;
}

/** `data.order_stats` from dashboard API */
function buildOverviewFromOrderStats(os) {
  if (!os || typeof os !== "object") return null;
  const n = (x) => {
    const v = Number(x);
    return Number.isFinite(v) ? v : 0;
  };
  return {
    "active-orders": n(
      os.total_order ??
        os.total_orders ??
        os.totalOrder ??
        os.pending_order ??
        os.pending_orders ??
        os.pendingOrder
    ),
    completed: n(
      os.delivered_order ??
        os.delivered_orders ??
        os.deliveredOrder ??
        os.completed_order ??
        os.completed_orders
    ),
    yesterday: n(os.yesterday_order ?? os.yesterday_orders ?? os.yesterdayOrder),
    today: n(os.today_order ?? os.today_orders ?? os.todayOrder),
  };
}

/** Months ending current month (oldest → newest), labels like "May 2026". */
function listMonthsEndingNow(count) {
  const out = [];
  const base = moment().startOf("month");
  for (let i = count - 1; i >= 0; i--) {
    const m = base.clone().subtract(i, "months");
    out.push({
      key: m.format("YYYY-MM"),
      label: m.format("MMMM YYYY"),
    });
  }
  return out;
}

/** Consecutive calendar days ending today (oldest → newest), DD-MM-YYYY labels. */
function listDaysEndingToday(dayCount) {
  const out = [];
  const end = moment().startOf("day");
  for (let i = dayCount - 1; i >= 0; i--) {
    const d = end.clone().subtract(i, "days");
    out.push({
      key: d.format("YYYY-MM-DD"),
      label: d.format("DD-MM-YYYY"),
    });
  }
  return out;
}


/**
 * Builds axis buckets when API omits `orders_trend` (zeros only), aligned with `type`.
 */
function buildTrendBuckets(apiType, customFromISO, customToISO) {
  if (apiType === "1 week") return listDaysEndingToday(7);
  if (apiType === "1 month") return listDaysEndingToday(30);
  if (apiType === "6 month") return listMonthsEndingNow(6);
  if (apiType === "1 year") return listMonthsEndingNow(12);

  const from = moment(customFromISO, "YYYY-MM-DD", true).startOf("day");
  const to = moment(customToISO, "YYYY-MM-DD", true).endOf("day");
  const fallback = listMonthsEndingNow(6);

  if (!from.isValid() || !to.isValid()) return fallback;

  let a = from;
  let b = to;
  if (a.isAfter(b)) {
    const t = a;
    a = b;
    b = t;
  }

  const days = b.diff(a, "days") + 1;

  if (days <= 45) {
    const out = [];
    for (let d = a.clone(); d.isSameOrBefore(b, "day"); d.add(1, "day")) {
      out.push({
        key: d.format("YYYY-MM-DD"),
        label: d.format("DD-MM-YYYY"),
      });
    }
    return out.length ? out : [{ key: a.format("YYYY-MM-DD"), label: a.format("DD-MM-YYYY") }];
  }

  const out = [];
  let cur = a.clone().startOf("month");
  const last = b.clone().startOf("month");
  while (cur.isSameOrBefore(last, "month")) {
    out.push({
      key: cur.format("YYYY-MM"),
      label: cur.format("MMMM YYYY"),
    });
    cur.add(1, "month");
  }
  return out.length ? out : [{ key: a.format("YYYY-MM-DD"), label: a.format("DD-MM-YYYY") }];
}

function normalizeOrdersCategoryCounts(response) {
  const candidates = [
    response?.data?.orders_by_type,
    response?.data?.ordersByType,
    response?.orders_by_type,
    response?.ordersByType,
    response?.chart?.orders_by_type,
    response?.category_order_counts,
  ];
  const raw = candidates.find((x) => Array.isArray(x) && x.length > 0);
  if (!raw) return null;
  const mapped = raw.map((x) => ({
    label: String(x.label ?? x.name ?? x.type ?? x.category ?? "—"),
    count: Number(x.count ?? x.value ?? x.orders ?? 0),
  }));
  const sum = mapped.reduce((s, x) => s + x.count, 0);
  return sum > 0 ? mapped : null;
}

const Dashboard = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { newTicketOrReply } = useSelector((state) => state.supportTicketState);
  const [stats, setStats] = useState([]);
  const [summaryRows, setSummaryRows] = useState([]);
  const [ordersCategoryCounts, setOrdersCategoryCounts] = useState(null);
  const [orderStats, setOrderStats] = useState(null);
  const [dashboardOrdersTrend, setDashboardOrdersTrend] = useState(null);
  const [initialLoading, setInitialLoading] = useState(true);
  const [chartLoading, setChartLoading] = useState(false);
  const [error, setError] = useState(null);
  const fetchSeqRef = useRef(0);
  const isFirstLoadRef = useRef(true);

  /** @type {[DashboardTrendType, function]} */
  const [trendRange, setTrendRange] = useState("6 month");
  const [customFrom, setCustomFrom] = useState(() =>
    moment().subtract(5, "days").format("YYYY-MM-DD")
  );
  const [customTo, setCustomTo] = useState(() => moment().format("YYYY-MM-DD"));

  const defaultStats = [
    { _id: 2, name: "Sales Person", count: 0, link: "/Salesperson", icon: FaUserTie },
    { _id: 3, name: "Wholesale User", count: 0, link: "/Wholesaleuser", icon: FaUsers },
    { _id: 6, name: "Retail User", count: 0, link: "/Retailusers", icon: FaUsers },
    { _id: 7, name: "Order", count: 0, link: "/Order", icon: FaShoppingCart },
    { _id: 8, name: "Category", count: 0, link: "/Categories", icon: FaTags },
    { _id: 9, name: "Product", count: 0, link: "/Product", icon: FaBox },
    { _id: 10, name: "Brand", count: 0, link: "/Brand", icon: FaStar },
    { _id: 11, name: "Banner", count: 0, link: "/Banner", icon: FaImage },
    {
      _id: 19,
      name: "Videos",
      apiNames: ["Videos", "Videos"],
      count: 0,
      link: "/videos",
      icon: FaVideo,
    },
    { _id: 12, name: "Country", count: 0, link: "/Country", icon: FaGlobe },
    { _id: 17, name: "Support Ticket", count: 0, link: "/Supportticket", icon: FaGlobe },
    {
      _id: 18,
      name: "Out Of Stock Products",
      count: 0,
      link: "/Product",
      icon: FaBox,
    },
  ];

  const fetchDashboardData = async () => {
    const seq = ++fetchSeqRef.current;
    const isFirst = isFirstLoadRef.current;
    try {
      if (isFirst) setInitialLoading(true);
      else setChartLoading(true);
      setError(null);
      const body = buildDashboardPostBody(trendRange, customFrom, customTo);
      const response = await getDashboardDataAPI(body);

      if (seq !== fetchSeqRef.current) return;

      if (response?.message === "User ID not found. Please login again.") {
        localStorage.removeItem("adminToken");
        localStorage.removeItem("adminData");
        localStorage.removeItem("userId");
        localStorage.removeItem("user_Id");
        window.location.href = "/login";
        return;
      }

      const ok =
        response?.status === 200 ||
        (response?.data != null && typeof response.data === "object");

      if (ok) {
        const rows = response?.data?.modules ?? [];
        setSummaryRows(Array.isArray(rows) ? rows : []);
        setOrderStats(response?.data?.order_stats ?? null);
        setDashboardOrdersTrend(
          Array.isArray(response?.data?.orders_trend) ? response.data.orders_trend : null
        );

        const updatedStats = defaultStats.map((defaultItem) => {
          const apiItem = rows.find((item) => {
            if (Array.isArray(defaultItem.apiNames) && defaultItem.apiNames.length > 0) {
              const apiName = String(item?.name ?? "")
                .trim()
                .toLowerCase();
              return defaultItem.apiNames.some(
                (n) => String(n).trim().toLowerCase() === apiName
              );
            }
            return item?.name === defaultItem?.name;
          });
          return apiItem ? { ...defaultItem, count: apiItem?.count } : defaultItem;
        });
        setStats(updatedStats);
        setOrdersCategoryCounts(normalizeOrdersCategoryCounts(response));

        if (response?.new_ticket > 0 || response?.new_message > 0) {
          dispatch(setNewTicketOrReply(1));
        } else {
          dispatch(setNewTicketOrReply(0));
        }
        setError(null);
      } else if (response?.message) {
        setError(
          typeof response?.message === "string"
            ? response.message
            : "Failed due to internal error"
        );
        setStats([]);
        setSummaryRows([]);
        setOrdersCategoryCounts(null);
        setOrderStats(null);
        setDashboardOrdersTrend(null);
      } else {
        setError("Failed due to internal error");
        setStats([]);
        setSummaryRows([]);
        setOrdersCategoryCounts(null);
        setOrderStats(null);
        setDashboardOrdersTrend(null);
      }
    } catch {
      if (seq !== fetchSeqRef.current) return;
      setError("Failed due to internal error");
      setStats([]);
      setSummaryRows([]);
      setOrdersCategoryCounts(null);
      setOrderStats(null);
      setDashboardOrdersTrend(null);
    } finally {
      if (seq === fetchSeqRef.current) {
        if (isFirst) {
          setInitialLoading(false);
          isFirstLoadRef.current = false;
        } else {
          setChartLoading(false);
        }
      }
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, [trendRange, customFrom, customTo]);

  const summaryCards = useMemo(() => {
    const fromOrders = orderStats ? buildOverviewFromOrderStats(orderStats) : null;
    return ORDER_SUMMARY_DEFS.map((def) => ({
      ...def,
      count:
        fromOrders != null
          ? (fromOrders[def.key] ?? 0)
          : pickCount(summaryRows, def.matchNames),
    }));
  }, [summaryRows, orderStats]);

  const trendBuckets = useMemo(
    () => buildTrendBuckets(trendRange, customFrom, customTo),
    [trendRange, customFrom, customTo]
  );

  const ordersTrend = useMemo(() => {
    if (Array.isArray(dashboardOrdersTrend) && dashboardOrdersTrend.length > 0) {
      return dashboardOrdersTrend.map((pt) => ({
        label: String(pt.label ?? pt.date_key ?? "—"),
        value: Number(pt.count ?? 0) || 0,
      }));
    }
    return trendBuckets.map(({ label }) => ({
      label,
      value: 0,
    }));
  }, [dashboardOrdersTrend, trendBuckets]);

  const trendSubtitle = useMemo(() => {
    const opt = TREND_RANGE_OPTIONS.find((o) => o.value === trendRange);
    let rangeLabel =
      trendRange === "custom"
        ? (opt?.label ?? "custom")
        : (opt?.label ?? trendRange);
    const hasTrend =
      Array.isArray(dashboardOrdersTrend) && dashboardOrdersTrend.length > 0;
    return hasTrend ? rangeLabel : `${rangeLabel} · no orders_trend in response`;
  }, [trendRange, customFrom, customTo, dashboardOrdersTrend]);

  const ordersTrendLabels = useMemo(
    () => ordersTrend.map((t) => t.label),
    [ordersTrend]
  );

  const ordersTrendValues = useMemo(
    () => ordersTrend.map((t) => t.value),
    [ordersTrend]
  );

  const chartRefreshKey = useMemo(() => {
    const pts = Array.isArray(dashboardOrdersTrend) ? dashboardOrdersTrend : [];
    const sig = pts.map((p) => `${p.date_key ?? ""}:${p.count ?? 0}`).join("|");
    return `${trendRange}:${customFrom}:${customTo}:${sig}`;
  }, [trendRange, customFrom, customTo, dashboardOrdersTrend]);

  if (initialLoading) {
    return (
      <div className="dashboard-loading">
        <div className="loading-spinner"></div>
        <p>Loading dashboard data...</p>
      </div>
    );
  }

  return (
    <div className="dashboard">
      <div className="dashboard-content">
        <section className="dashboard-top-overview" aria-label="Order overview">
          <div className="dashboard-order-cards">
            {summaryCards.map(({ key, label, count, link, Icon, iconTone }) => (
              <button
                type="button"
                key={key}
                className="dashboard-order-stat-card"
                onClick={() => navigate(link)}
              >
                <div className={`dashboard-order-stat-icon ${iconTone}`}>
                  <Icon aria-hidden />
                </div>
                <div className="dashboard-order-stat-body">
                  <span className="dashboard-order-stat-count">{count}</span>
                  <span className="dashboard-order-stat-label">{label}</span>
                </div>
              </button>
            ))}
          </div>

          <OrdersCharts
            categoryCounts={ordersCategoryCounts}
            trendLabels={ordersTrendLabels}
            trendValues={ordersTrendValues}
            trendRange={trendRange}
            onTrendRangeChange={setTrendRange}
            customFromDate={customFrom}
            customToDate={customTo}
            onCustomFromDateChange={setCustomFrom}
            onCustomToDateChange={setCustomTo}
            trendRangeOptions={TREND_RANGE_OPTIONS}
            trendSubtitle={trendSubtitle}
            trendLoading={chartLoading}
            chartRefreshKey={chartRefreshKey}
            onViewOrders={() => navigate("/Order")}
          />
        </section>

        <div className="card-container">
          {stats?.map((item) => (
            <div
              className="card"
              key={item?._id + item?.name}
              onClick={async () => {
                if (item?.name?.toLowerCase() === "support ticket") {
                  try {
                    await updateSupportTicketReadStatusAPI();
                  } catch (err) {
                    console.log("Error updating support ticket read status:", err);
                  }
                }
                if (item?.name?.toLowerCase() === "out of stock products") {
                  navigate(item.link, { state: { filter: "out_of_stock" } });
                } else {
                  navigate(item.link);
                }
              }}
              style={{ cursor: "pointer" }}
            >
              {item?.name?.toLowerCase() === "support ticket" &&
                newTicketOrReply > 0 && (
                  <span className="dashoboard-ticket-dot"></span>
                )}
              <div className="card-icon">{item?.count}</div>
              <p className="card-label">{item?.name}</p>
            </div>
          ))}
        </div>

        {error && (
          <div className="error-message">
            <p>{error}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
